import type {
  GetKanbanConfigsResponse,
  StartIterationSyncResponse,
  StartProgramBacklogSyncResponse,
} from "@/backend/natsstubs/almsync/models";

import { BackendError } from "../BackendError";
import type {
  ServerAlmSource,
  ServerAlmStickyTypeData,
  ServerBacklogSyncState,
  ServerIterationSyncState,
  ServerKanbanConfigMap,
} from "../serverModel";

/**
 * `alm_types.get`, `alm_item_type.*` and `project_sources.get` are `rawResponse` subjects:
 * the payload is the ALM tool's own record (item-type maps keyed by board keys, source ids
 * and transition hashes; per-team/ART source lists), so the wire shape arrives untouched —
 * snake_case, exactly what the WAMP procedures returned. These helpers only take off the
 * response envelope; a malformed one is a BackendError like every other transport failure.
 */
function unexpectedShape(subject: string, expected: string, response: unknown): BackendError {
  return new BackendError("AlmResponseShapeError", `almsync ${subject}: expected ${expected}`, {
    request: {},
    response: { received: response },
  });
}

export function unwrapAlmTypes(response: unknown): ServerAlmStickyTypeData[] {
  const list = (response as { alm_types?: unknown } | null)?.alm_types;
  if (!Array.isArray(list)) {
    throw unexpectedShape("alm_types.get", "{ alm_types: [...] }", response);
  }
  return list as ServerAlmStickyTypeData[];
}

export function unwrapAlmItemType<T extends object>(response: unknown, subject: string): T {
  const item = (response as { alm_item_type?: unknown } | null)?.alm_item_type;
  if (item === null || item === undefined || typeof item !== "object" || Array.isArray(item)) {
    throw unexpectedShape(subject, "{ alm_item_type: {...} }", response);
  }
  return item as T;
}

export function unwrapProjectSources(response: unknown): ServerAlmSource[] {
  const list = (response as { project_sources?: unknown } | null)?.project_sources;
  if (!Array.isArray(list)) {
    throw unexpectedShape("project_sources.get", "{ project_sources: [...] }", response);
  }
  return list as ServerAlmSource[];
}

/**
 * The legacy sync procedures answered `{ success, status, error }` and reported failures
 * in-band; over NATS a refused sync is an error response, so a returned status always means
 * success (`syncing` = a sync of this target was already running).
 */
export function mapApiIterationSyncState(
  response: StartIterationSyncResponse,
): ServerIterationSyncState {
  return { success: true, status: response.status, error: "" };
}

export function mapApiBacklogSyncState(
  response: StartProgramBacklogSyncResponse,
): ServerBacklogSyncState {
  return { success: true, status: response.status };
}

/** JSON object keys are strings; the legacy map is keyed by the numeric team id. */
export function mapApiKanbanConfigs(response: GetKanbanConfigsResponse): ServerKanbanConfigMap {
  return Object.fromEntries(
    Object.entries(response.configs).map(([teamId, configs]) => [Number(teamId), configs]),
  );
}
